import React, { useState, useCallback } from 'react';
import { VStack, Box } from '@chakra-ui/react';
import Media from '@components/atoms/Media';
import Link from '@components/atoms/Link';
import Button from '@components/atoms/Button';
import BubbleTail from '@components/atoms/BubbleTail';
import { motion } from 'framer-motion';

const LinkRoute = ({
  nodeData,
  isPassive = false,
  onNodeClicked = () => {}
}) => {
  const {
    answer: { answerText, image },
    result
  } = nodeData;

  const handleOnLinkRoute = useCallback(() => {
    if (isPassive) {
      return;
    }

    onNodeClicked(nodeData);
  }, [isPassive, nodeData, onNodeClicked]);

  return (
    <Link
      href={`/finder-results/${result.slug}`}
      flex="0"
      w="100%"
      p="0"
      variant="answer"
      fontSize="md"
      whiteSpace="normal"
      display="block"
      lineHeight="1.5rem"
      pointerEvents={isPassive ? `none` : ``}
      onClick={handleOnLinkRoute}>
      {image !== null && <Media maxH="12.375rem" media={image} />}
      <Box as="span" p="0.5rem" display="block">
        {answerText}
      </Box>
    </Link>
  );
};

const JourneyRoute = ({
  nodeData,
  erronous,
  isPassive = false,
  onJourneyClicked = () => {}
}) => {
  const {
    answer: { answerText, image },
    journey
  } = nodeData;

  const [wasSelected, setWasSelected] = useState(false);
  const handleOnButtonRoute = useCallback(() => {
    if (isPassive || wasSelected) {
      return;
    }

    setWasSelected(true);
    onJourneyClicked(journey.id, nodeData);
  }, [isPassive, wasSelected, journey, nodeData, onJourneyClicked]);

  return (
    <Button
      flex="0"
      w="100%"
      h="auto"
      p="0"
      variant="answer"
      fontSize="md"
      whiteSpace="normal"
      display="block"
      lineHeight="1.5rem"
      cursor={isPassive || wasSelected ? `default` : `pointer`}
      onClick={handleOnButtonRoute}
      isActive={wasSelected}
      disabled={erronous}>
      {image !== null && <Media maxH="12.375rem" media={image} />}
      <Box as="span" p="0.5rem" display="block">
        {answerText}
        {erronous && ` (action missing)`}
      </Box>
    </Button>
  );
};

const NodeNavigation = ({
  node,
  isPassive,
  onNodeClicked = () => {},
  onJourneyClicked = () => {}
}) => {
  const { result, journey } = node;
  const erronous = result === null && journey === null;

  return result !== null && !erronous ? (
    <LinkRoute
      nodeData={node}
      isPassive={isPassive}
      onNodeClicked={onNodeClicked}
    />
  ) : (
    <JourneyRoute
      erronous={erronous}
      nodeData={node}
      isPassive={isPassive}
      onJourneyClicked={onJourneyClicked}
    />
  );
};

const AnswerNode = ({
  data,
  staggerTime = 0.5,
  itemAnim,
  onNodeClicked = () => {},
  onJourneyClicked = () => {},
  onAnimationComplete = () => {}
}) => {
  const { answerItems = [], separateAnswers = false } = data;
  const [isPassive, setIsPassive] = useState(false);

  const handleJourneyClicked = useCallback(
    (id, { answer }) => {
      setIsPassive(true);
      onJourneyClicked(id, {
        answer: answer.answerText
      });
    },
    [onJourneyClicked]
  );

  const handleOnNodeClicked = useCallback(
    ({ answer }) =>
      onNodeClicked({
        answer: answer.answerText
      }),
    [onNodeClicked]
  );

  return separateAnswers ? (
    answerItems.map((node, i) => (
      <Box
        key={node.id}
        ml="auto"
        mb={i >= answerItems.length - 1 ? `1rem` : 0}
        maxW="18.5rem"
        w="100%"
        // Motion props
        as={motion.div}
        custom={i * staggerTime}
        initial="nodeInitial"
        animate="nodeAnimate"
        variants={itemAnim}
        onAnimationComplete={() => onAnimationComplete(i)}>
        <Box
          p="1rem"
          mt={i > 0 ? `1rem` : `0`}
          pos="relative"
          bg="white"
          color="purple.600"
          fontWeight="600"
          borderRadius=".5rem">
          <NodeNavigation
            node={node}
            isPassive={isPassive}
            onNodeClicked={handleOnNodeClicked}
            onJourneyClicked={handleJourneyClicked}
          />
          <BubbleTail
            bgColour="white"
            bottom="12px"
            left="auto"
            right="-8px"
            direction="right"
          />
        </Box>
      </Box>
    ))
  ) : (
    <Box
      ml="auto"
      maxW="18.5rem"
      w="100%"
      // Motion props
      as={motion.div}
      initial="nodeInitial"
      animate="nodeAnimate"
      variants={itemAnim}>
      <VStack
        p="1rem"
        mb="1rem"
        pos="relative"
        bg="white"
        color="purple.600"
        fontWeight="600"
        borderRadius=".5rem">
        {answerItems.map((node) => (
          <NodeNavigation
            key={node.id}
            node={node}
            isPassive={isPassive}
            onNodeClicked={handleOnNodeClicked}
            onJourneyClicked={handleJourneyClicked}
          />
        ))}
        <BubbleTail
          bgColour="white"
          bottom="12px"
          left="auto"
          right="-8px"
          direction="right"
        />
      </VStack>
    </Box>
  );
};

const AnswerNodeShell = ({
  data,
  isVisible = false,
  index,
  itemAnim,
  onNodeClicked,
  onJourneyClicked,
  onAnimationStarted = () => {},
  onAnimationComplete = () => {}
}) => {
  return (
    <Box
      as={motion.div}
      initial="shellInitial"
      variants={itemAnim}
      custom={index}
      animate={isVisible ? `shellAnimate` : ``}
      onAnimationStart={() => onAnimationStarted()}
      onAnimationComplete={() => onAnimationComplete(index)}>
      <AnswerNode
        data={data}
        onNodeClicked={onNodeClicked}
        onJourneyClicked={onJourneyClicked}
      />
    </Box>
  );
};

export default AnswerNodeShell;
